"use client"

import { useQuery } from "@tanstack/react-query"
import { createContext, type ReactNode, useContext } from "react"
import { authClient } from "@/lib/auth/auth-client"
import { useORPC } from "@/providers/orpc-provider"

// 用户状态接口
interface UserState {
	hasSession: boolean
	isNewUser: boolean
	isLoading: boolean
}

// 创建用户状态上下文
const UserStateContext = createContext<UserState | null>(null)

// 用户状态 Hook
export function useUserState(): UserState {
	const context = useContext(UserStateContext)
	if (!context) {
		throw new Error("useUserState must be used within AdvertisementWrapper")
	}
	return context
}

interface AdvertisementWrapperProps {
	children: ReactNode
}

/**
 * 广告包装组件
 * 统一管理用户状态，避免重复请求
 */
export function AdvertisementWrapper({ children }: AdvertisementWrapperProps) {
	const orpc = useORPC()
	const { data: session } = authClient.useSession()

	// 统一获取用户设置
	const { data: userSettings, isLoading: isUserSettingsLoading } = useQuery({
		...orpc.user.settings.getSingleSetting.queryOptions({
			input: {
				flagName: "IsNewUser",
			},
		}),
		staleTime: 5 * 60 * 1000, // 5分钟缓存
		enabled: !!session,
	})

	const userState: UserState = {
		hasSession: !!session,
		isNewUser: userSettings?.IsNewUser || false,
		isLoading: isUserSettingsLoading,
	}

	return (
		<UserStateContext.Provider value={userState}>
			{children}
		</UserStateContext.Provider>
	)
}
